import React from "react";
import { ShieldCheck, FlaskConical, Truck, MapPin, ClipboardCheck, Lock } from "lucide-react";

/**
 * "Trusted by British researchers" trust-signal grid.
 *
 * This component intentionally contains NO customer testimonials. Every claim
 * below is a verifiable, substantiable statement of operational fact —
 * required under ASA / CAP Code rule 3.45 (testimonials must be genuine and
 * verifiable). Until we have real reviews flowing in from Trustpilot or
 * Google Reviews, we lean on facts.
 *
 * Each tile maps to evidence we can produce if challenged:
 *   • Purity → COA PDFs on file (admin → products → COA)
 *   • Same-day dispatch → 14:00 cutoff documented on /same-day-dispatch
 *   • Royal Mail tracking → dispatch emails include tracking number
 *   • UK Ltd → Companies House registered entity (footer)
 *   • Research-use-only → site-wide age gate + product disclaimers
 *   • Sealed, padded packaging → standard fulfilment SOP
 */
const PILLARS = [
  {
    icon: FlaskConical,
    title: "Independently purity-tested",
    body:
      "Every batch is tested by an independent third-party testing laboratory. The Certificate of Analysis records UPLC/MS purity against the unique batch number printed on the vial. COAs are available on request.",
    proof: "View sample COA on any product page",
  },
  {
    icon: MapPin,
    title: "UK stock, UK dispatch",
    body:
      "All vials held in our United Kingdom facility. No international forwarding, no customs hold-ups, no surprise import VAT.",
    proof: "Held in the UK, shipped from the UK",
  },
  {
    icon: Truck,
    title: "Same-day Royal Mail",
    body:
      "Orders placed before 14:00 GMT on a working day are dispatched the same day via Royal Mail Tracked 24 with full tracking emailed on dispatch.",
    proof: "14:00 dispatch cutoff documented",
  },
  {
    icon: ClipboardCheck,
    title: "Batch documentation",
    body:
      "Every vial carries a unique batch number cross-referenced to its supplier COA, purity result and test date. Full chain of documentation kept on file.",
    proof: "Batch number printed on every vial",
  },
  {
    icon: Lock,
    title: "Sealed, padded packaging",
    body:
      "Plain padded mailers with no externally visible product or molecule references. Tamper-evident seal on every parcel.",
    proof: "Generic outer packaging by default",
  },
  {
    icon: ShieldCheck,
    title: "UK Limited Company",
    body:
      "Peptigen Labs Ltd is a registered United Kingdom limited company supplying laboratory materials strictly for in vitro research use.",
    proof: "Companies House registered entity",
  },
];

function Pillar({ icon: Icon, title, body, proof }) {
  return (
    <div
      className="pg-card p-6 flex flex-col h-full"
      data-testid={`trust-pillar-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
          style={{ background: "rgba(166,255,0,0.10)", border: "1px solid rgba(166,255,0,0.3)" }}
        >
          <Icon size={16} className="text-[#A6FF00]" />
        </div>
        <h3
          className="text-[#F7FAFC] pg-heading text-base leading-tight"
          style={{ fontFamily: "'Rajdhani'", fontWeight: 700 }}
        >
          {title}
        </h3>
      </div>
      <p className="text-[#9CA8B8] text-sm leading-relaxed flex-1">{body}</p>
      <div className="mt-4 pt-3 border-t border-[#00C2D6]/15">
        <div className="pg-mono text-[10px] uppercase tracking-[0.18em] text-[#5EEAD4]">
          {proof}
        </div>
      </div>
    </div>
  );
}

export default function Reviews({ heading = true }) {
  return (
    <section className="relative" data-testid="trust-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {heading && (
          <div className="mb-10 max-w-3xl">
            <div className="pg-micro mb-3">// Why researchers choose us</div>
            <h2
              className="text-[#F7FAFC] text-3xl lg:text-4xl pg-display"
              style={{ fontFamily: "'Exo 2'" }}
            >
              Trusted by British researchers
            </h2>
            <p className="text-[#9CA8B8] mt-3 leading-relaxed">
              Six operational standards we hold ourselves to on every order. No
              fabricated testimonials, no inflated star-counts — just the
              documentation, dispatch and compliance facts that matter.
            </p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map((p) => (
            <Pillar key={p.title} {...p} />
          ))}
        </div>
      </div>
    </section>
  );
}
